
import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  // Handle scroll to add background/shadow when the page is scrolled
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    // Cleanup listener on unmount
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const navLinks = [
    { name: 'Home', path: '/', icon: 'ri-home-4-line' },
    { name: 'Services', path: '/services', icon: 'ri-settings-3-line' },
    { name: 'Overview', path: '/overview', icon: 'ri-building-2-line' },
    { name: 'Contact', path: '/contact', icon: 'ri-mail-send-line' },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled
          ? 'bg-gray-950/95 backdrop-blur-xl shadow-[0_4px_30px_rgba(0,0,0,0.3)]'
          : 'bg-gray-950/90 backdrop-blur-lg'
      }`}
    >
      {/* Subtle racing stripe accent at very top */}
      <div className="h-[2px] w-full bg-gradient-to-r from-transparent via-orange-500 to-transparent opacity-80"></div>

      <div className="w-full">
        <div className="flex items-center justify-between px-5 lg:px-10 h-[76px]">
          {/* Left: Logo + Company Name */}
          <Link
            to="/"
            className="flex items-center gap-3.5 shrink-0 cursor-pointer group"
          >
            <div className="relative">
              <img
                src="https://static.wixstatic.com/media/e8bd68_4f47684bd62b49c281e7c2c4b23733c4~mv2.png/v1/crop/x_0,y_1038,w_4500,h_2462/fill/w_187,h_105,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/png.png"
                alt="iBen Consulting Logo"
                className="h-10 w-auto transition-transform duration-300 group-hover:scale-105"
              />
            </div>
            <div className="flex flex-col">
              <span className="text-white font-bold text-[15px] tracking-wide leading-tight">
                iBen Consulting
              </span>
              <span className="text-orange-400/70 text-[10px] font-semibold tracking-[0.2em] uppercase leading-tight">
                IT Solutions
              </span>
            </div>
          </Link>

          {/* Center: Navigation */}
          <nav className="hidden lg:flex items-center">
            <div className="flex items-center bg-white/[0.06] rounded-full px-1.5 py-1.5 border border-white/[0.06]">
              {navLinks.map((link) => {
                const isActive = location.pathname === link.path;
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`relative px-5 py-2 text-[13px] font-semibold tracking-[0.06em] transition-all duration-300 cursor-pointer rounded-full whitespace-nowrap ${
                      isActive
                        ? 'text-gray-950 bg-orange-500 shadow-lg shadow-orange-500/25'
                        : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    {link.name}
                  </Link>
                );
              })}
            </div>
          </nav>

          {/* Right: Action Buttons */}
          <div className="hidden lg:flex items-center gap-2.5">
            <a
              href="tel:+16506704236"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-[13px] font-semibold tracking-wide transition-all duration-300 whitespace-nowrap cursor-pointer text-gray-400 hover:text-white"
            >
              <div className="w-7 h-7 rounded-full bg-white/[0.08] flex items-center justify-center">
                <i className="ri-phone-line w-3.5 h-3.5 flex items-center justify-center text-orange-400 text-xs"></i>
              </div>
              (650) 670-4236
            </a>

            <div className="w-px h-6 bg-white/10"></div>

            <a
              href="https://calendar.app.google/EUjmYRGaBXHNxWB49"
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-[13px] font-semibold tracking-wide transition-all duration-300 whitespace-nowrap cursor-pointer border border-white/15 text-gray-300 hover:border-orange-500/50 hover:text-white hover:bg-white/[0.04]"
            >
              <i className="ri-calendar-check-line w-4 h-4 flex items-center justify-center text-sm text-orange-400"></i>
              Book Appointment
            </a>

            <a
              href="https://iben.rmmservice.com/connect/#/"
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-5 py-2.5 rounded-full text-[13px] font-semibold tracking-wide transition-all duration-300 whitespace-nowrap cursor-pointer shadow-lg shadow-orange-500/20 hover:shadow-orange-500/35 hover:-translate-y-[1px]"
            >
              Remote Support
              <i className="ri-remote-control-line w-4 h-4 flex items-center justify-center text-sm"></i>
            </a>
          </div>

          {/* Mobile: Hamburger */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden w-10 h-10 flex items-center justify-center rounded-full cursor-pointer transition-all duration-300 text-white hover:bg-white/10"
            aria-label="Toggle menu"
          >
            <i
              className={`${
                isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-3-line'
              } text-xl`}
            ></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`lg:hidden overflow-hidden transition-all duration-400 ease-in-out ${
          isMobileMenuOpen ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="px-5 pb-6 pt-2 bg-gray-950/98 backdrop-blur-xl border-t border-white/[0.06]">
          <div className="flex flex-col gap-1 mb-5">
            {navLinks.map((link) => {
              const isActive = location.pathname === link.path;
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3.5 rounded-xl text-sm font-semibold tracking-wide transition-all duration-200 cursor-pointer ${
                    isActive
                      ? 'text-orange-400 bg-orange-500/10'
                      : 'text-gray-400 hover:text-white hover:bg-white/[0.04]'
                  }`}
                >
                  <div
                    className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                      isActive ? 'bg-orange-500/20' : 'bg-white/[0.06]'
                    }`}
                  >
                    <i
                      className={`${link.icon} text-sm ${
                        isActive ? 'text-orange-400' : 'text-gray-500'
                      }`}
                    ></i>
                  </div>
                  {link.name}
                  {isActive && (
                    <span className="ml-auto w-1.5 h-1.5 rounded-full bg-orange-500"></span>
                  )}
                </Link>
              );
            })}
          </div>

          <div className="flex flex-col gap-2.5 pt-4 border-t border-white/[0.06]">
            <a
              href="tel:+16506704236"
              className="flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer text-gray-400 hover:text-white"
            >
              <i className="ri-phone-line w-4 h-4 flex items-center justify-center text-sm text-orange-400"></i>
              (650) 670-4236
            </a>
            <a
              href="https://calendar.app.google/EUjmYRGaBXHNxWB49"
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer border border-white/15 text-gray-300 hover:border-orange-500/50 hover:text-white"
            >
              <i className="ri-calendar-check-line w-4 h-4 flex items-center justify-center text-sm text-orange-400"></i>
              Book Appointment
            </a>
            <a
              href="https://iben.rmmservice.com/connect/#/"
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 text-white py-3 rounded-xl text-sm font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer shadow-lg shadow-orange-500/20"
            >
              Remote Support
              <i className="ri-remote-control-line w-4 h-4 flex items-center justify-center text-sm"></i>
            </a>
          </div>
        </div>
      </div>
    </header>
  );
}
